import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { CATEGORICAL_COLORS } from './categoricalColors';

export interface StackedBarSeries {
    key: string;
    label: string;
    color?: string;
}

export interface StackedBarDataPoint {
    group: string;
    label?: string;
    [seriesKey: string]: string | number | undefined;
}

interface StackedBarChartProps {
    title: string;
    tooltipText?: string;
    titleClassName?: string;
    data: StackedBarDataPoint[];
    series: StackedBarSeries[];
    loading?: boolean;
    height?: number;
    yFormatter?: (value: number) => string;
    // false renders the series side by side instead of stacked, for comparing values rather than summing them
    stacked?: boolean;
}

// Renders the rounded, dark tooltip shown on hover
const CustomTooltip: React.FC<any> = ({ active, payload, label, yFormatter }) => {
    if (!active || !payload || !payload.length) {
        return null;
    }

    return (
        <div className='time-series-tooltip'>
            <div className='tooltip-label'>{label}</div>
            {payload.map((entry: any) => (
                <div key={entry.dataKey} className='tooltip-row'>
                    <span className='tooltip-dot' style={{ backgroundColor: entry.color }} />
                    <span className='tooltip-name'>{entry.name}</span>
                    <span className='tooltip-value'>{yFormatter ? yFormatter(entry.value) : entry.value}</span>
                </div>
            ))}
        </div>
    );
};

const StackedBarChart: React.FC<StackedBarChartProps> = ({
    title, tooltipText, titleClassName, data, series, loading, height = 220, yFormatter, stacked = true,
}) => {
    const { t } = useTranslation();

    // The chart is only truly empty when every group has no data at all
    const hasData = data.some(point => series.some(item => Number(point[item.key]) > 0));

    return (
        <div className='metric-chart-widget stacked-bar-chart'>
            <div className='metric-chart-header'>
                <h6 className={titleClassName}>
                    {title}
                    {tooltipText && (
                        <span className='tooltip-icon'>
                            <FontAwesomeIcon icon={faCircleQuestion} />
                            <span className='tooltip tooltip-right'>{tooltipText}</span>
                        </span>
                    )}
                </h6>
            </div>
            {loading ? (
                <div className='metric-chart-loading' style={{ height }}>
                    <div className='loader' />
                </div>
            ) : !hasData ? (
                <div className='metric-chart-empty' style={{ height }}>{t('general.no_data_available')}</div>
            ) : (
                <>
                    <ResponsiveContainer width='100%' height={height}>
                        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 24 }}>
                            <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='var(--border-color-light)' />
                            <XAxis
                                dataKey='label'
                                tick={{ fontSize: 11, fill: 'var(--text-color-lightgray)', fontFamily: 'var(--main-font-light)' }}
                                axisLine={{ stroke: 'var(--border-color-light)' }}
                                tickLine={false}
                                interval={0}
                                angle={-35}
                                textAnchor='end'
                                height={50}
                            />
                            <YAxis
                                tickFormatter={yFormatter}
                                tick={{ fontSize: 11, fill: 'var(--text-color-lightgray)', fontFamily: 'var(--main-font-light)' }}
                                axisLine={false}
                                tickLine={false}
                                width={40}
                            />
                            <Tooltip content={<CustomTooltip yFormatter={yFormatter} />} cursor={{ fill: 'var(--background-color-gray)' }} />
                            {series.map((item, index) => (
                                <Bar
                                    key={item.key}
                                    dataKey={item.key}
                                    name={item.label}
                                    stackId={stacked ? 'stack' : undefined}
                                    fill={item.color || CATEGORICAL_COLORS[index % CATEGORICAL_COLORS.length]}
                                    radius={!stacked || index === series.length - 1 ? [4, 4, 0, 0] : undefined}
                                    isAnimationActive
                                    animationDuration={400}
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                    <div className='donut-chart-legend'>
                        {series.map((item, index) => (
                            <div key={item.key} className='donut-chart-legend-item'>
                                <span className='dot' style={{ backgroundColor: item.color || CATEGORICAL_COLORS[index % CATEGORICAL_COLORS.length] }} />
                                <span>{item.label}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default StackedBarChart;
